// Client-side XLSX download helper. Wraps `write-excel-file` so
// consumers get a one-call API matching the shape of shared/csv's
// `toCsv` but producing a browser-triggered XLSX download instead.
//
// We picked write-excel-file over SheetJS (xlsx) for active maintenance
// and tighter bundle size (~70 kB gzipped vs ~300+).
//
// Usage:
//   await downloadXlsx({
//     fileName: "landing-pages.xlsx",
//     sheetName: "Landing pages",
//     columns: ["Landing page", "Sessions", "Conversions"],
//     rows: [ ["/pricing", 1234, 12], ["/", 800, 5] ],
//   });

// The browser entry emits a Blob + triggers the download — the node entry
// requires fs. We explicitly target /browser so Vite doesn't pick up the
// Node variant via the `types` conditional first.
import writeXlsxFile from "write-excel-file/browser";

export type XlsxCell = string | number | null | undefined;

export interface XlsxSheet {
  /** Excel sheet tab name — max 31 chars per spec; we truncate. */
  sheetName: string;
  /** Column headers for the top row. */
  columns: string[];
  /** 2-D array of cell values, row-major. Column count must match `columns`. */
  rows: XlsxCell[][];
}

export interface DownloadXlsxParams {
  fileName: string;
  sheets: XlsxSheet[];
}

function toHeaderCells(columns: string[]) {
  return columns.map((label) => ({
    value: label,
    fontWeight: "bold" as const,
    backgroundColor: "#F4F2FF",
    color: "#0E1680",
    borderColor: "#DCD6FF",
  }));
}

function toDataCells(row: XlsxCell[]) {
  return row.map((cell) => {
    if (cell === null || cell === undefined) return { value: "", type: String };
    if (typeof cell === "number") {
      return {
        value: cell,
        type: Number,
        format: Number.isInteger(cell) ? "#,##0" : "#,##0.00",
      };
    }
    return { value: String(cell), type: String };
  });
}

export async function downloadXlsx({ fileName, sheets }: DownloadXlsxParams): Promise<void> {
  if (sheets.length === 0) throw new Error("downloadXlsx: at least one sheet required");
  const data = sheets.map((s) => [toHeaderCells(s.columns), ...s.rows.map(toDataCells)]);
  // The library's multi-sheet types are finicky — cast through `any` at
  // the call boundary since the shapes match at runtime. Single-sheet
  // callers still type-check normally because they use an array of length 1.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const options: any = {
    fileName,
    sheets: sheets.map((s) => s.sheetName.slice(0, 31)),
    columns: sheets.map((s) => s.columns.map(() => ({ width: 20 }))),
  };
  await writeXlsxFile(data as never, options);
}
